import React, {useEffect, useState} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';

import {useAuthStore} from '@/entities/session/auth-store';
import {ApiError} from '@/shared/api';
import {messageOf} from '@/shared/lib/errors';
import type {AuthNav} from '@/app/routes';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card, IconBubble} from '@/shared/ui/card';
import {Icon} from '@/shared/ui/icons';
import {AppInput} from '@/shared/ui/input';
import {Screen} from '@/shared/ui/screen';
import {AppText, useLinkText} from '@/shared/ui/text';
import {controls, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Код из письма после регистрации (или после входа в брошенную регистрацию).
 *
 * Почта и пароль — в auth-store, только в памяти: пароль сервер сверяет при
 * подтверждении, а в адрес страницы и на диск он попасть не должен. Открыли экран
 * по ссылке без начатой регистрации — возвращаем на форму: подтверждать нечего.
 *
 * Сессию выдаёт подтверждение; навигацию дальше делает корневой навигатор по
 * статусу сессии, как после входа.
 */
const styles = StyleSheet.create({
  body: {justifyContent: 'center', gap: spacing(5), paddingVertical: spacing(8)},
  hero: {alignItems: 'center', gap: spacing(3)},
  heroText: {alignItems: 'center', gap: spacing(1), paddingHorizontal: spacing(4)},
  centered: {textAlign: 'center'},
  form: {gap: spacing(4)},
  link: {minHeight: controls.lg, justifyContent: 'center', alignSelf: 'center'},
});

export function VerifyEmailScreen() {
  const {t} = useTranslation();
  const p = useTheme();
  const link = useLinkText();
  const nav = useNavigation<AuthNav>();
  const signup = useAuthStore(s => s.signup);
  const confirmSignup = useAuthStore(s => s.confirmSignup);
  const resendSignup = useAuthStore(s => s.resendSignup);
  const cancelSignup = useAuthStore(s => s.cancelSignup);

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!signup) nav.navigate('Register');
  }, [signup, nav]);

  // Таймер «Отправить ещё раз»: подсказка сервера, ограничивает он сам числом писем.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!signup) return null;

  const resendIn = Math.max(
    0,
    Math.ceil((signup.sentAt + signup.resendAfterSecs * 1000 - now) / 1000),
  );

  async function submit() {
    if (!code.trim()) {
      setError(t('auth.errors.codeRequired'));
      return;
    }
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      await confirmSignup(code.trim());
    } catch (e) {
      if (e instanceof ApiError && e.status === 422 && e.message.includes('password_mismatch')) {
        setError(t('auth.verify.passwordMismatch'));
      } else if (e instanceof ApiError && e.status === 422) {
        setError(t('auth.verify.badCode'));
      } else if (e instanceof ApiError && e.status === 429) {
        setError(t('auth.verify.codeBurned'));
      } else {
        setError(messageOf(e, t('errors.network')));
      }
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setError(null);
    setInfo(null);
    try {
      await resendSignup();
      setInfo(t('auth.verify.resent'));
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 429
          ? t('auth.verify.tooMany')
          : messageOf(e, t('errors.network')),
      );
    }
  }

  return (
    <Screen scroll width="narrow" style={styles.body} testID="verify-email-screen">
      <View style={styles.hero}>
        <IconBubble tone={p.accent} size={56} square>
          <Icon name="mail" size={28} color={p.primary} />
        </IconBubble>
        <View style={styles.heroText}>
          <AppText variant="display" style={styles.centered}>
            {t('auth.verify.title')}
          </AppText>
          <AppText variant="body" color={p.mutedForeground} style={styles.centered}>
            {t('auth.verify.subtitle', {
              email: signup.email,
              minutes: Math.round(signup.expiresInSecs / 60),
            })}
          </AppText>
        </View>
      </View>

      <Card padding={5} style={styles.form}>
        <AppInput
          testID="verify-code"
          label={t('auth.verify.code')}
          placeholder="123456"
          value={code}
          onChangeText={setCode}
          keyboardType="number-pad"
          autoCapitalize="none"
          autoCorrect={false}
          onSubmitEditing={submit}
          returnKeyType="go"
        />

        {error ? <Banner testID="verify-error" tone="error" title={error} /> : null}
        {info ? <Banner testID="verify-info" tone="success" title={info} /> : null}

        <AppButton
          testID="verify-submit"
          title={t('auth.verify.submit')}
          size="lg"
          onPress={submit}
          loading={busy}
        />

        <AppButton
          testID="verify-resend"
          title={
            resendIn > 0
              ? t('auth.verify.resendIn', {seconds: resendIn})
              : t('auth.verify.resend')
          }
          tone="ghost"
          disabled={resendIn > 0}
          onPress={resend}
        />

        <Pressable
          testID="verify-restart"
          style={styles.link}
          accessibilityRole="link"
          onPress={() => {
            cancelSignup();
            nav.navigate('Register');
          }}>
          <AppText variant="label" style={link}>
            {t('auth.verify.restart')}
          </AppText>
        </Pressable>
      </Card>
    </Screen>
  );
}
